import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus, Globe, Hammer, HardHat, Sparkles, BellRing } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { money, planOptions, planBillingSuffix, planTermNote, planIsCancelAnytime } from '@/data/constants';
import {
  membershipCopy,
  perLeadCents,
  breakEvenLine,
  PLAN_INCLUDES_WEBSITE,
} from '@/lib/membershipValue';
import { track } from '@/lib/tracking';
import Reveal from '@/components/home/Reveal';

/**
 * "WHY BECOME A MEMBER?" — homepage section 4, placed right after the visitor
 * has seen real open projects, which is where the buy decision is made.
 *
 * Only real benefits are shown: monthly lead credits, the lower effective
 * per-lead cost, MEMBER-FIRST job alerts (job-alerts-digest v3.1, live
 * 2026-10-14) and the free business website on Premium. Every number is
 * derived from src/lib/membershipValue.ts -> src/data/constants.ts.
 * The single-lead path stays visible so the section never feels like a trap,
 * and the credit-expiry + Premium-term disclosure is always on screen.
 *
 * BASIC50 intro ribbon: intentionally NOT rendered. As of 2026-10-14 the code
 * is not redeemable on the live Basic checkout (mkt_coupons.ghl_sync_state =
 * 'needs_ghl_scope', checkout-link returns couponError
 * 'coupon_paused_processor_switch' on the Square-backed Basic link). Add the
 * ribbon (introOfferLine() -> /pricing?plan=basic&coupon=BASIC50) only after
 * that probe returns a valid coupon.
 */
const MOST_POPULAR = 'pro';

const MembershipValue: React.FC = () => {
  const { path, lang } = useI18n();
  const copy = membershipCopy(lang);
  const plans = planOptions(lang);

  const rows: { label: string; single: React.ReactNode; member: React.ReactNode }[] = [
    { label: copy.rowPerLead, single: copy.singlePerLead, member: <strong className="text-emerald-700">{copy.memberPerLead}</strong> },
    { label: copy.rowBundle, single: copy.singleBundle, member: <strong className="text-emerald-700">{copy.memberBundle}</strong> },
    {
      label: copy.rowRefill,
      single: <Minus className="mx-auto h-4 w-4 text-slate-300" aria-label="no" />,
      member: <Check className="mx-auto h-5 w-5 text-emerald-600" aria-label="yes" />,
    },
    { label: copy.rowFirst, single: copy.firstSingle, member: <strong className="text-emerald-700">{copy.firstMember}</strong> },
    { label: copy.rowCommit, single: copy.commitNone, member: copy.commitMonthly },
  ];

  return (
    <section id="membership" className="bg-white py-16" aria-labelledby="membership-title">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-orange ring-1 ring-orange-200">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> {copy.sectionEyebrow}
          </span>
          <h2 id="membership-title" className="mt-3 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {copy.sectionTitle}
          </h2>
          <p className="mt-3 text-base text-slate-600">{copy.sectionSub}</p>
          {/* MEMBER-FIRST JOB ALERTS — live benefit (job-alerts-digest v3.1). */}
          <p className="mx-auto mt-4 inline-flex max-w-2xl items-start gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-left text-sm text-emerald-900 ring-1 ring-emerald-200">
            <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            <span>
              <strong>{copy.memberFirst}.</strong> {copy.memberFirstDetail}
            </span>
          </p>
        </Reveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-5">
          {/* LEFT — single lead vs member comparison */}
          <Reveal className="lg:col-span-2">
            <div className="h-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-white">
                    <th className="px-4 py-3 text-left font-semibold text-slate-500" scope="col"><span className="sr-only">—</span></th>
                    <th className="px-3 py-3 text-center font-bold text-slate-700" scope="col">{copy.colSingle}</th>
                    <th className="bg-emerald-50 px-3 py-3 text-center font-extrabold text-emerald-800" scope="col">{copy.colMember}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {rows.map((r) => (
                    <tr key={r.label}>
                      <th scope="row" className="px-4 py-3 text-left font-semibold text-slate-700">{r.label}</th>
                      <td className="px-3 py-3 text-center text-slate-600">{r.single}</td>
                      <td className="bg-emerald-50/60 px-3 py-3 text-center text-slate-800">{r.member}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="grid gap-3 border-t border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <div className="flex gap-2.5">
                  <Hammer className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" aria-hidden="true" />
                  <p className="text-xs leading-relaxed text-slate-600">
                    <strong className="block text-sm text-slate-900">{copy.whyContractorTitle}</strong>
                    {copy.whyContractor}
                  </p>
                </div>
                <div className="flex gap-2.5">
                  <HardHat className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
                  <p className="text-xs leading-relaxed text-slate-600">
                    <strong className="block text-sm text-slate-900">{copy.whySubTitle}</strong>
                    {copy.whySub}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — three plan cards from planOptions() */}
          <div className="grid gap-4 sm:grid-cols-3 lg:col-span-3">
            {plans.map((p, i) => {
              const popular = p.code === MOST_POPULAR;
              return (
                <Reveal key={p.code} delay={i * 80}>
                  <div
                    className={`relative flex h-full flex-col rounded-2xl border p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-card-hover ${
                      popular ? 'border-orange-300 bg-orange-50/50 shadow-card ring-1 ring-orange-200' : 'border-slate-200 bg-white shadow-sm'
                    }`}
                  >
                    {popular && (
                      <span className="absolute -top-3 left-5 rounded-full bg-brand-orange px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-white shadow">
                        {copy.popular}
                      </span>
                    )}
                    <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">{p.name}</h3>
                    <p className="mt-1 text-3xl font-black text-slate-900">
                      {money(p.price)}
                      <span className="text-sm font-semibold text-slate-500">{planBillingSuffix(p.code, lang)}</span>
                    </p>
                    <p className="mt-1 text-sm font-bold text-orange-600">
                      {p.leads} {copy.leadsPerMonth}
                    </p>
                    <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-700">
                      <li className="flex gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                        <span>
                          <strong>{money(perLeadCents(p.code))}</strong> {copy.perLeadSuffix} · {copy.savePct(p.code)}
                        </span>
                      </li>
                      <li className="flex gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                        <span>{breakEvenLine(p.code, lang)}</span>
                      </li>
                      <li className="flex gap-2">
                        <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                        <span>{copy.memberFirst}</span>
                      </li>
                      {PLAN_INCLUDES_WEBSITE[p.code] && (
                        <li className="flex gap-2 font-semibold text-slate-900">
                          <Globe className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" aria-hidden="true" />
                          <span>{copy.websiteIncluded}</span>
                        </li>
                      )}
                    </ul>
                    <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                      {planIsCancelAnytime(p.code)
                        ? lang === 'es'
                          ? 'Mensual — cancele cuando quiera.'
                          : 'Monthly — cancel anytime.'
                        : planTermNote(p.code, p.price, lang)}
                    </p>
                    <Link
                      to={`${path('/pricing')}?plan=${p.code}`}
                      onClick={() => track('cta_click', { action: 'home_member_section_plan', plan: p.code })}
                      className={`mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 ${
                        popular ? 'bg-brand-orange text-white hover:bg-orange-600' : 'bg-brand-navy text-white hover:bg-slate-800'
                      }`}
                    >
                      {lang === 'es' ? `Elegir ${p.name}` : `Choose ${p.name}`} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to={path('/pricing')}
            onClick={() => track('cta_click', { action: 'home_member_section_plans' })}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-3.5 text-base font-bold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600"
          >
            {copy.ctaPlans} <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
          <Link
            to={path('/jobs')}
            onClick={() => track('cta_click', { action: 'home_member_section_single' })}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-base font-bold text-slate-800 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-400"
          >
            {copy.ctaSingle}
          </Link>
        </div>

        {/* Always-visible disclosure next to the savings claims. */}
        <p className="mx-auto mt-5 max-w-3xl text-center text-xs leading-relaxed text-slate-500">{copy.disclosure}</p>
      </div>
    </section>
  );
};

export default MembershipValue;
